import React, { lazy, useContext } from 'react'
import { Outlet, Route, Routes } from 'react-router-dom'
import { Layout } from './pages/Layout'
import { Main } from './pages/Main'
import { ROUTE_NAMES } from 'shared/consts'
import { AccountContext } from 'core/context'
import { LanguageProvider } from 'context/LanguageContext'

// Страницы подгружаются по требованию: в стартовый бандл попадают только
// каркас (Layout) и главная.  Кабинет, торговые экраны с графиками, админка и
// остальные страницы приезжают отдельными файлами при переходе на них —
// первая загрузка сайта не тянет код, который посетителю не нужен.
const Account = lazy(() =>
    import('./pages/Account').then(m => ({ default: m.Account }))
)
const AdminSymbolProposals = lazy(() =>
    import('./pages/AdminSymbolProposals').then(m => ({ default: m.AdminSymbolProposals }))
)
const AdminPartnerRequests = lazy(() =>
    import('./pages/AdminPartnerRequests').then(m => ({ default: m.AdminPartnerRequests }))
)
const Binance = lazy(() =>
    import('./pages/Binance').then(m => ({ default: m.Binance }))
)
const BinanceAccounts = lazy(() =>
    import('./pages/BinanceAccounts').then(m => ({ default: m.BinanceAccounts }))
)
const BinanceSymbols = lazy(() =>
    import('./pages/BinanceSymbols').then(m => ({ default: m.BinanceSymbols }))
)
const BinanceTrade = lazy(() =>
    import('./pages/BinanceTrade').then(m => ({ default: m.BinanceTrade }))
)
const Bybit = lazy(() =>
    import('./pages/Bybit').then(m => ({ default: m.Bybit }))
)
const BybitAccounts = lazy(() =>
    import('./pages/BybitAccounts').then(m => ({ default: m.BybitAccounts }))
)
const BybitSymbols = lazy(() =>
    import('./pages/BybitSymbols').then(m => ({ default: m.BybitSymbols }))
)
const BybitTrade = lazy(() =>
    import('./pages/BybitTrade').then(m => ({ default: m.BybitTrade }))
)
const Bitget = lazy(() =>
    import('./pages/Bitget').then(m => ({ default: m.Bitget }))
)
const BitgetAccounts = lazy(() =>
    import('./pages/BitgetAccounts').then(m => ({ default: m.BitgetAccounts }))
)
const BitgetSymbols = lazy(() =>
    import('./pages/BitgetSymbols').then(m => ({ default: m.BitgetSymbols }))
)
const BitgetTrade = lazy(() =>
    import('./pages/BitgetTrade').then(m => ({ default: m.BitgetTrade }))
)
const CryptoMarkets = lazy(() =>
    import('./pages/CryptoMarkets').then(m => ({ default: m.CryptoMarkets }))
)
const ErrorPage = lazy(() =>
    import('./pages/ErrorPage').then(m => ({ default: m.ErrorPage }))
)
const GlobalMarkets = lazy(() =>
    import('./pages/GlobalMarkets').then(m => ({ default: m.GlobalMarkets }))
)
const Guides = lazy(() =>
    import('./pages/Guides').then(m => ({ default: m.Guides }))
)
const Okx = lazy(() =>
    import('./pages/Okx').then(m => ({ default: m.Okx }))
)
const OkxAccounts = lazy(() =>
    import('./pages/OkxAccounts').then(m => ({ default: m.OkxAccounts }))
)
const OkxSymbols = lazy(() =>
    import('./pages/OkxSymbols').then(m => ({ default: m.OkxSymbols }))
)
const OkxTrade = lazy(() =>
    import('./pages/OkxTrade').then(m => ({ default: m.OkxTrade }))
)
const Partner = lazy(() =>
    import('./pages/Partner').then(m => ({ default: m.Partner }))
)
const PartnerClients = lazy(() =>
    import('./pages/PartnerClients').then(m => ({ default: m.PartnerClients }))
)
const PartnerClientTrade = lazy(() =>
    import('./pages/PartnerClientTrade').then(m => ({ default: m.PartnerClientTrade }))
)
const Comments = lazy(() =>
    import('./pages/Comments').then(m => ({ default: m.Comments }))
)
const Premium = lazy(() =>
    import('./pages/Premium').then(m => ({ default: m.Premium }))
)
const PublicReport = lazy(() =>
    import('./pages/PublicReport').then(m => ({ default: m.PublicReport }))
)
const Referral = lazy(() =>
    import('./pages/Referral').then(m => ({ default: m.Referral }))
)
const Staking = lazy(() =>
    import('./pages/Staking').then(m => ({ default: m.Staking }))
)
const Architecture = lazy(() =>
    import('./pages/Architecture').then(m => ({ default: m.Architecture }))
)
const Book = lazy(() =>
    import('./pages/Book').then(m => ({ default: m.Book }))
)
const Rithmic = lazy(() =>
    import('./pages/Rithmic').then(m => ({ default: m.Rithmic }))
)
const RithmicAccounts = lazy(() =>
    import('./pages/RithmicAccounts').then(m => ({ default: m.RithmicAccounts }))
)
const RithmicSymbols = lazy(() =>
    import('./pages/RithmicSymbols').then(m => ({ default: m.RithmicSymbols }))
)
const RithmicTrade = lazy(() =>
    import('./pages/RithmicTrade').then(m => ({ default: m.RithmicTrade }))
)
const TT = lazy(() =>
    import('./pages/TT').then(m => ({ default: m.TT }))
)
const TTAccounts = lazy(() =>
    import('./pages/TTAccounts').then(m => ({ default: m.TTAccounts }))
)
const TTSymbols = lazy(() =>
    import('./pages/TTSymbols').then(m => ({ default: m.TTSymbols }))
)
const TTTrade = lazy(() =>
    import('./pages/TTTrade').then(m => ({ default: m.TTTrade }))
)
const CQG = lazy(() =>
    import('./pages/CQG').then(m => ({ default: m.CQG }))
)
const CQGAccounts = lazy(() =>
    import('./pages/CQGAccounts').then(m => ({ default: m.CQGAccounts }))
)
const CQGSymbols = lazy(() =>
    import('./pages/CQGSymbols').then(m => ({ default: m.CQGSymbols }))
)
const CQGTrade = lazy(() =>
    import('./pages/CQGTrade').then(m => ({ default: m.CQGTrade }))
)
const TermsAndConditions = lazy(() =>
    import('./pages/TermsAndConditions').then(m => ({ default: m.TermsAndConditions }))
)

export const App: React.FC = React.memo(() => {
    const { accountData, isAuthReady } = useContext(AccountContext)

    return (
        <LanguageProvider>
            <Routes>
                <Route path="/" element={<Layout />}>
                    <Route index path={ROUTE_NAMES.MAIN} element={<Main />} />
                    <Route
                        path={ROUTE_NAMES.TERMS}
                        element={<TermsAndConditions />}
                    />
                    <Route path={ROUTE_NAMES.GUIDES} element={<Guides />} />
                    <Route path={`${ROUTE_NAMES.GUIDES}/:category`} element={<Guides />} />
                    <Route
                        path={ROUTE_NAMES.GLOBAL_MARKETS}
                        element={<GlobalMarkets />}
                    />
                    <Route
                        path={ROUTE_NAMES.CRYPTO_MARKETS}
                        element={<CryptoMarkets />}
                    />
                    <Route path={ROUTE_NAMES.PREMIUM} element={<Premium />} />
                    {/* Публичный отчёт по всем сделкам — открыт без входа. */}
                    <Route
                        path={ROUTE_NAMES.REPORT}
                        element={<PublicReport />}
                    />
                    {/* Все отзывы пользователей: читают все, пишут вошедшие. */}
                    <Route
                        path={ROUTE_NAMES.COMMENTS}
                        element={<Comments />}
                    />
                    {accountData && (
                        <Route path={ROUTE_NAMES.TRADING} element={<Outlet />}>
                            <Route
                                path={ROUTE_NAMES.BINANCE}
                                element={<Binance />}
                            />
                            <Route
                                path={ROUTE_NAMES.RITHMIC}
                                element={<Rithmic />}
                            />
                            <Route
                                path={ROUTE_NAMES.BYBIT}
                                element={<Bybit />}
                            />
                            <Route
                                path={ROUTE_NAMES.BITGET}
                                element={<Bitget />}
                            />
                            <Route
                                path={ROUTE_NAMES.OKX}
                                element={<Okx />}
                            />
                            <Route
                                path={ROUTE_NAMES.TT}
                                element={<TT />}
                            />
                            <Route
                                path={ROUTE_NAMES.CQG}
                                element={<CQG />}
                            />
                        </Route>
                    )}
                    {accountData && (
                        <Route
                            path={ROUTE_NAMES.ACCOUNT}
                            element={<Account />}
                        />
                    )}
                    {/* Staking / Referral / Partner — открыты для гостей в
                        режиме предпросмотра: формы видны, активация после
                        регистрации (см. GuestPreviewBanner на страницах). */}
                    <Route path={ROUTE_NAMES.STAKING} element={<Staking />} />
                    <Route path={ROUTE_NAMES.REFERRAL} element={<Referral />} />
                    <Route path={ROUTE_NAMES.PARTNER} element={<Partner />} />

                    {accountData?.isPartner && (
                        <Route
                            path={ROUTE_NAMES.PARTNER_CLIENTS}
                            element={<Outlet />}
                        >
                            <Route
                                path={ROUTE_NAMES.BINANCE}
                                element={<Outlet />}
                            >
                                <Route
                                    index
                                    element={
                                        <PartnerClients exchange="binance" />
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <PartnerClientTrade exchange="binance" />
                                    }
                                />
                            </Route>
                            <Route
                                path={ROUTE_NAMES.BYBIT}
                                element={<Outlet />}
                            >
                                <Route
                                    index
                                    element={
                                        <PartnerClients exchange="bybit" />
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <PartnerClientTrade exchange="bybit" />
                                    }
                                />
                            </Route>
                            <Route
                                path={ROUTE_NAMES.OKX}
                                element={<Outlet />}
                            >
                                <Route
                                    index
                                    element={
                                        <PartnerClients exchange="okx" />
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <PartnerClientTrade exchange="okx" />
                                    }
                                />
                            </Route>
                            <Route
                                path={ROUTE_NAMES.RITHMIC}
                                element={<Outlet />}
                            >
                                <Route
                                    index
                                    element={
                                        <PartnerClients exchange="rithmic" />
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <PartnerClientTrade exchange="rithmic" />
                                    }
                                />
                            </Route>
                        </Route>
                    )}
                    {accountData?.isAdmin && (
                        <>
                            <Route
                                path={ROUTE_NAMES.PARTNER_REQUESTS}
                                element={<AdminPartnerRequests />}
                            />
                            <Route
                                path={ROUTE_NAMES.SYMBOL_PROPOSALS}
                                element={<AdminSymbolProposals />}
                            />
                            <Route
                                path={ROUTE_NAMES.ARCHITECTURE}
                                element={<Architecture />}
                            />
                            <Route
                                path={ROUTE_NAMES.BOOK}
                                element={<Book />}
                            />
                            <Route
                                path={ROUTE_NAMES.BINANCE}
                                element={<Outlet />}
                            >
                                <Route
                                    path={`${ROUTE_NAMES.TRADE}/:id`}
                                    element={<BinanceTrade />}
                                />
                                <Route
                                    path={ROUTE_NAMES.ACCOUNTS}
                                    element={<BinanceAccounts />}
                                />
                                <Route
                                    path={ROUTE_NAMES.SYMBOLS}
                                    element={<BinanceSymbols />}
                                />
                            </Route>
                            <Route
                                path={ROUTE_NAMES.RITHMIC}
                                element={<Outlet />}
                            >
                                <Route
                                    path={ROUTE_NAMES.ACCOUNTS}
                                    element={<RithmicAccounts />}
                                />
                                <Route
                                    path={`${ROUTE_NAMES.TRADE}/:id`}
                                    element={<RithmicTrade />}
                                />
                                <Route
                                    path={ROUTE_NAMES.SYMBOLS}
                                    element={<RithmicSymbols />}
                                />
                            </Route>
                            <Route
                                path={ROUTE_NAMES.BYBIT}
                                element={<Outlet />}
                            >
                                <Route
                                    path={`${ROUTE_NAMES.TRADE}/:id`}
                                    element={<BybitTrade />}
                                />
                                <Route
                                    path={ROUTE_NAMES.ACCOUNTS}
                                    element={<BybitAccounts />}
                                />
                                <Route
                                    path={ROUTE_NAMES.SYMBOLS}
                                    element={<BybitSymbols />}
                                />
                            </Route>
                            <Route
                                path={ROUTE_NAMES.BITGET}
                                element={<Outlet />}
                            >
                                <Route
                                    path={`${ROUTE_NAMES.TRADE}/:id`}
                                    element={<BitgetTrade />}
                                />
                                <Route
                                    path={ROUTE_NAMES.ACCOUNTS}
                                    element={<BitgetAccounts />}
                                />
                                <Route
                                    path={ROUTE_NAMES.SYMBOLS}
                                    element={<BitgetSymbols />}
                                />
                            </Route>
                            <Route
                                path={ROUTE_NAMES.OKX}
                                element={<Outlet />}
                            >
                                <Route
                                    path={`${ROUTE_NAMES.TRADE}/:id`}
                                    element={<OkxTrade />}
                                />
                                <Route
                                    path={ROUTE_NAMES.ACCOUNTS}
                                    element={<OkxAccounts />}
                                />
                                <Route
                                    path={ROUTE_NAMES.SYMBOLS}
                                    element={<OkxSymbols />}
                                />
                            </Route>
                            <Route
                                path={ROUTE_NAMES.TT}
                                element={<Outlet />}
                            >
                                <Route
                                    path={`${ROUTE_NAMES.TRADE}/:id`}
                                    element={<TTTrade />}
                                />
                                <Route
                                    path={ROUTE_NAMES.ACCOUNTS}
                                    element={<TTAccounts />}
                                />
                                <Route
                                    path={ROUTE_NAMES.SYMBOLS}
                                    element={<TTSymbols />}
                                />
                            </Route>
                            <Route
                                path={ROUTE_NAMES.CQG}
                                element={<Outlet />}
                            >
                                <Route
                                    path={`${ROUTE_NAMES.TRADE}/:id`}
                                    element={<CQGTrade />}
                                />
                                <Route
                                    path={ROUTE_NAMES.ACCOUNTS}
                                    element={<CQGAccounts />}
                                />
                                <Route
                                    path={ROUTE_NAMES.SYMBOLS}
                                    element={<CQGSymbols />}
                                />
                            </Route>
                        </>
                    )}
                    {isAuthReady && (
                        <Route path="/*" element={<ErrorPage errorCode="404" />} />
                    )}
                </Route>
            </Routes>
        </LanguageProvider>
    )
})
